/**
 * EmailActionConfirmationPage — Sprint 9.
 *
 * Public route at `/email-action/confirm`. Displayed when a manager
 * clicks an approve / discuss / hold action link from an email
 * notification. No authentication required.
 *
 * Query params (Zod-validated):
 *   status     — "approved" | "discuss" | "hold" | "expired" | "invalid"
 *   leave_id   — optional UUID (shown in a tabular-mono pill)
 *   actor_name — optional string (name of the person who acted)
 *
 * Status → visual mapping:
 *   approved → sage check icon
 *   discuss  → ochre clock icon
 *   hold     → ochre clock icon
 *   expired  → clay alert icon
 *   invalid  → clay alert icon
 *   unknown  → falls back to "invalid" (clay)
 *
 * Feature flag: flags.inlineApprovalCard (defaults OFF).
 * When OFF: redirect to "/".
 *
 * Design rules: token-only colors, 44px hit target on link button,
 * focus-visible ring in --primary, prefers-reduced-motion safe.
 */
import { z } from 'zod'
import { Navigate, useSearch } from '@tanstack/react-router'
import { flags } from '../../lib/feature-flags'

// ── Query param schema ────────────────────────────────────────────────────────

const STATUS_VALUES = ['approved', 'discuss', 'hold', 'expired', 'invalid'] as const

type ConfirmStatus = (typeof STATUS_VALUES)[number]

const confirmParamsSchema = z.object({
  status: z.enum(STATUS_VALUES).catch('invalid'),
  leave_id: z.string().optional(),
  actor_name: z.string().optional(),
})

type ConfirmParams = z.infer<typeof confirmParamsSchema>

// ── Status → content ──────────────────────────────────────────────────────────

type IconVariant = 'sage' | 'ochre' | 'clay'

interface StatusContent {
  heading: string
  iconVariant: IconVariant
  /** Icon Unicode character */
  icon: string
}

function resolveStatus(status: ConfirmStatus): StatusContent {
  switch (status) {
    case 'approved':
      return {
        heading: 'Decision recorded — leave approved',
        iconVariant: 'sage',
        icon: '✓',
      }
    case 'discuss':
      return {
        heading: "Marked for discussion — we'll follow up",
        iconVariant: 'ochre',
        icon: '○',
      }
    case 'hold':
      return {
        heading: 'Put on hold',
        iconVariant: 'ochre',
        icon: '○',
      }
    case 'expired':
      return {
        heading: 'This link has expired',
        iconVariant: 'clay',
        icon: '⚠',
      }
    case 'invalid':
    default:
      return {
        heading: 'This link is no longer valid (or already used)',
        iconVariant: 'clay',
        icon: '⚠',
      }
  }
}

// ── Token helpers ─────────────────────────────────────────────────────────────

interface VariantTokens {
  bg: string
  border: string
  color: string
  headerBg: string
}

function variantTokens(variant: IconVariant): VariantTokens {
  switch (variant) {
    case 'sage':
      return {
        bg: 'var(--success-bg, rgba(88,140,100,0.08))',
        border: 'var(--success-line, rgba(88,140,100,0.25))',
        color: 'var(--success-fg, #2d6a3f)',
        headerBg: 'var(--success-bg, rgba(88,140,100,0.12))',
      }
    case 'ochre':
      return {
        bg: 'var(--warning-bg, rgba(200,155,30,0.08))',
        border: 'var(--warning-line, rgba(200,155,30,0.28))',
        color: 'var(--warning-fg, #8a5e00)',
        headerBg: 'var(--warning-bg, rgba(200,155,30,0.12))',
      }
    case 'clay':
    default:
      return {
        bg: 'var(--danger-bg, rgba(180,60,50,0.08))',
        border: 'var(--danger-line, rgba(180,60,50,0.25))',
        color: 'var(--danger-fg, #8b2020)',
        headerBg: 'var(--danger-bg, rgba(180,60,50,0.12))',
      }
  }
}

// ── Inner card ────────────────────────────────────────────────────────────────

interface ConfirmCardProps {
  params: ConfirmParams
}

function ConfirmCard({ params }: ConfirmCardProps): JSX.Element {
  const content = resolveStatus(params.status)
  const tokens = variantTokens(content.iconVariant)

  return (
    <div
      data-testid="email-action-card"
      style={{
        maxWidth: 480,
        width: '100%',
        background: 'var(--paper-raised, #fff)',
        border: `1px solid ${tokens.border}`,
        borderRadius: 'var(--r-lg, 12px)',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(80,56,28,0.08)',
      }}
    >
      {/* Terracotta header bar */}
      <div
        style={{
          padding: '18px 24px',
          background: 'var(--primary, #c3523a)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span
          data-testid="status-icon"
          data-variant={content.iconVariant}
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: tokens.bg,
            border: `1px solid ${tokens.border}`,
            color: tokens.color,
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {content.icon}
        </span>
        <h1
          data-testid="status-heading"
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 600,
            color: 'var(--paper, #fff)',
            fontFamily: 'var(--font-sans)',
            lineHeight: 1.3,
          }}
        >
          {content.heading}
        </h1>
      </div>

      {/* Body */}
      <div
        style={{
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* leave_id pill */}
        {params.leave_id != null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)', flexShrink: 0 }}>
              Leave
            </span>
            <span
              data-testid="leave-id-pill"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-2)',
                background: 'rgba(107,99,89,0.08)',
                border: '1px solid rgba(107,99,89,0.18)',
                borderRadius: 4,
                padding: '2px 8px',
                userSelect: 'all',
              }}
            >
              {params.leave_id}
            </span>
          </div>
        ) : null}

        {/* actor_name */}
        {params.actor_name != null ? (
          <p
            data-testid="actor-name"
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            {params.actor_name}
          </p>
        ) : null}

        {/* Open LMS button */}
        <a
          data-testid="open-lms-link"
          href="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 44,
            padding: '0 24px',
            borderRadius: 'var(--r-sm, 6px)',
            background: 'var(--primary, #c3523a)',
            color: 'var(--paper, #fff)',
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
            alignSelf: 'flex-start',
            outline: 'none',
          }}
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          onFocus={(e) => {
            ;(e.currentTarget as HTMLElement).style.boxShadow =
              '0 0 0 3px var(--primary, #c3523a), 0 0 0 5px var(--paper, #fff)'
          }}
          onBlur={(e) => {
            ;(e.currentTarget as HTMLElement).style.boxShadow = 'none'
          }}
        >
          Open Leave Hub
        </a>
      </div>
    </div>
  )
}

// ── Public export ─────────────────────────────────────────────────────────────

/**
 * EmailActionConfirmationPage.
 *
 * PUBLIC — no auth required. Gated on `flags.inlineApprovalCard` only.
 * When flag is OFF: redirect to "/".
 */
export function EmailActionConfirmationPage(): JSX.Element | null {
  if (!flags.inlineApprovalCard) return <Navigate to="/" />

  // useSearch returns the raw query string object; parse via Zod
  const rawSearch = useSearch({ strict: false }) as Record<string, unknown>
  const params = confirmParamsSchema.parse({
    status: rawSearch['status'],
    leave_id: rawSearch['leave_id'],
    actor_name: rawSearch['actor_name'],
  })

  return (
    <div
      data-testid="email-action-page"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        background: 'var(--paper, #FAF7F2)',
      }}
    >
      <ConfirmCard params={params} />
    </div>
  )
}
